import { useEffect, useState } from 'react';
import { Button, Input, Space, Switch, Table, Tabs } from "antd";
import ordersStorage from "../../../storages/orders.storage";
import styles from "../orders-page.module.scss";
import { ordersTableColumns } from "../../../helpers/tables";
import DetailTable from "../components/detailTable";
import { observer } from 'mobx-react-lite';
import partTypesStorage from "../../../storages/part-types";
import layoutStorage, { Dialogs } from "../../../storages/layout-storage";

function Orders() {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterByPart, setFilterByPart] = useState<string>('');
  const [filterByReceived, setFilterByReceived] = useState<boolean>(false);
  const [filterByWrongStock, setFilterByWrongStock] = useState<boolean>(false);
  const {
    currentOrders,
    loadOrders,
    mode,
    activeKey,
    setActiveKey,
    expandRow,
    expandTrackingRow,
    clearExpandedRowKeys,
    setStatusFilter,
    statusFilter,
  } = ordersStorage;

  const categories = new Set<string>(['all']);
  const parts = new Set<string>(['all']);
  const statuses = new Set<string>(['all']);

  currentOrders
    .filter((order) => statusFilter !== 'all' ? order.orderStatus === statusFilter : true)
    .forEach((order) => {
      order.orderItems.forEach((item) => {
        categories.add(item.category || 'Uncategorized');
        parts.add(item.part || 'Uncategorized');
      });
    });

  currentOrders
    .filter((order) => activeKey !== 'all' ? order.category === activeKey : true)
    .forEach((order) => {
      statuses.add(order.orderStatus);
    });

  const loadData = async () => {
    partTypesStorage
      .loadPartTypes()
      .then(() => {
        ordersStorage.loadOrders();
      });
  }

  useEffect(() => { loadData() }, []);

  const onStatusTabChange = (key: string) => {
    setStatusFilter(key);
    setCurrentPage(1);
    clearExpandedRowKeys();
  }

  return (
    <>
      <Tabs
        type="editable-card"
        onChange={onStatusTabChange}
        activeKey={statusFilter}
        items={[...statuses].map((item) => ({ label: item, key: item }))}
      />
      <Space align="center" style={{display: 'flex', justifyContent: 'space-between'}}>
        <Space>
          <Button type="primary" onClick={loadOrders}>Refresh</Button>
          <Button type="primary" onClick={() => layoutStorage.toggleDialog(Dialogs.AddOrderDialog)}>Add order</Button>
        </Space>
      </Space>
      <Space>
        <Switch
          checkedChildren="All"
          unCheckedChildren="Uncategorized"
          defaultChecked={mode === 'all'}
          onChange={(checked) => {
            ordersStorage.mode = checked ? 'all' : 'uncategorized';
          }}
        />
        <Switch
          checkedChildren="Not received only"
          unCheckedChildren="All (received and not rectived)"
          // defaultChecked={false}
          value={filterByReceived}
          onChange={(checked) => {
            setFilterByReceived(checked);
          }}
        />
        <Switch
          checkedChildren="Wrong stock only"
          unCheckedChildren="All (wrong stock and not wrong stock)"
          // defaultChecked={false}
          value={filterByWrongStock}
          onChange={(checked) => {
            setFilterByWrongStock(checked);
          }}
        />
      </Space>
      <Space>
        <Input
          value={ordersStorage.searchTerm}
          placeholder="Search"
          onChange={(event) => {
            ordersStorage.setSearch(event.target.value);
          }}
        />
      </Space>
      <Space>{activeKey}</Space>
      <Table
        rowKey="id"
        className={styles['tableMiddle']}
        columns={ordersTableColumns.map(({dataIndex, ...column}) => ({
          ...column,
          dataIndex,
          // ...getColumnSearchProps<IOrder>(AllowedSections.orders, dataIndex, currentOrders),
        }))}
        dataSource={currentOrders.filter((order) => {
          const used: Record<string, number> = order.orderItems.reduce((acc, item) => {
            if (!item || !item.used || !item._id) return acc;
            acc[item._id] = item.used.reduce((acc, usedItem) => {
              return acc + usedItem.quantity;
            }, 0);
            return acc;
          }, {} as Record<string, number>);

          return (
            (filterByWrongStock ? order.orderItems.some((item) => {
              const used = item.used?.reduce((acc, usedItem) => {
                return acc + usedItem.quantity;
              }, 0) || 0;
              return used > (item.inStock || 0);
            }) : true)
            && (activeKey !== 'all' ? order.orderItems.some((item) => (item.category === activeKey)) : true)
            && (filterByPart && filterByPart !== 'all' ? order.orderItems.some((item) => (item.part === filterByPart)) : true)
            && (statusFilter !== 'all' ? order.orderStatus === statusFilter : true)
            && (filterByReceived ? order.orderItems.some((item) => !item.received) : true)
            // && (order.orderItems?.some((item) => !item.partTypes || item.partTypes.length === 0 || !item.shortName))
            // && order.orderItems.some((item) => (item.inStock || 0) - used[item._id || 0] < 0)
            && (ordersStorage.searchTerm ? (
              order.marketplaceId?.toLowerCase().includes(ordersStorage.searchTerm.toLowerCase())
              || order.sellerId?.toLowerCase().includes(ordersStorage.searchTerm.toLowerCase())
              || order.orderItems.some((item) => {
                const parsedSearchTerm = ordersStorage.searchTerm.toLowerCase();
                return (
                  item.itemText?.toLowerCase().includes(parsedSearchTerm)
                  || item.shortName?.toLowerCase().includes(parsedSearchTerm)
                  || item._id?.toLowerCase().includes(parsedSearchTerm)
                  || order.orderId?.toLowerCase().includes(parsedSearchTerm)
                  || order.trackingNumbers?.some((trackingNumber) => trackingNumber?.toLowerCase().includes(parsedSearchTerm))
                )
              })
            ) : true)
            // && order.orderItems.some((item) => !item.shortName)
          );
        })}
        rowClassName={(order) => {
          const isOverused = false;

          for (const item of order.orderItems) {
            const used = item.used?.reduce((acc, usedItem) => {
              return acc + usedItem.quantity;
            }, 0) || 0;
            if (used > (item.inStock || 0)) {
              return styles['overused'];
            }
          }

          const inStock = order.orderItems?.some((item) => item.received);
          const notInStock = order.orderItems?.some((item) => !item.received);

          if (inStock && notInStock) {
            return styles['partially-in-stock'];
          }
          if (inStock) {
            return styles['in-stock'];
          }
          return styles['not-in-stock'];
        }}
        pagination={
          {
            position: ['bottomCenter'],
            // pageSize: 20,
            current: currentPage,
            onChange: (page) => {
              setCurrentPage(page);
            }
          }
        }
        expandable={{
          expandedRowRender: (order,  index, indent, expanded) => {
            return (
              expanded && <DetailTable order={order} key={order._id} />
            );
          },
          rowExpandable: (record) => {
            // if (mode === 'uncategorized') {
            //   return record.orderItems?.some((item) => !item.category || !item.partTypes || item.partTypes?.length === 0);
            // }

            return record.orderItems?.length > 0;
          },
          // defaultExpandedRowKeys: expandedRowKeys,
          // expandedRowKeys: expandedRowKeys,
          onExpandedRowsChange: (expandedRows) => {
            console.log("expandedRows", expandedRows);
          },
          onExpand: (expanded, record) => {
            expandRow(record._id, expanded);
          }
        }}
      />
    </>
  );
}

export default observer(Orders);
